<template>
    <Transition enter-active-class="transition-all duration-500 ease-out" enter-from-class="translate-y-full opacity-0"
        enter-to-class="translate-y-0 opacity-100" leave-active-class="transition-all duration-300 ease-in"
        leave-from-class="translate-y-0 opacity-100" leave-to-class="translate-y-full opacity-0">
        <div v-if="showBanner" class="fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6" role="dialog"
            aria-labelledby="cookie-banner-title" aria-describedby="cookie-banner-description">

            <!-- Banner Content -->
            <div class="relative mx-auto max-w-7xl">
                <div class="rounded-2xl bg-white shadow-2xl ring-1 ring-gray-900/10">
                    <div class="p-6 sm:p-8 md:flex md:items-start md:gap-8">
                        <!-- Cookie Icon -->
                        <div class="hidden md:flex md:shrink-0">
                            <div
                                class="flex h-14 w-14 items-center justify-center rounded-xl bg-primary-500 shadow-theme-layout">
                                <svg class="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                            </div>
                        </div>

                        <!-- Text Content -->
                        <div class="flex-1 md:mt-0">
                            <h3 id="cookie-banner-title" class="text-lg font-semibold text-gray-900 sm:text-xl">
                                We value your privacy
                            </h3>
                            <p id="cookie-banner-description"
                                class="mt-2 text-sm leading-relaxed text-gray-600 sm:text-base">
                                We use cookies and similar technologies to enhance your browsing experience, analyze
                                site traffic, and personalize content.
                                By clicking "Accept", you consent to our use of cookies.
                                <button @click="showDetails = !showDetails"
                                    class="ml-1 inline-flex items-center font-medium text-primary-500 hover:text-primary-600"
                                    type="button" aria-expanded="showDetails">
                                    {{ showDetails ? 'Show less' : 'Learn more' }}
                                    <svg class="ml-1 h-4 w-4 transition-transform duration-200"
                                        :class="{ 'rotate-180': showDetails }" fill="none" viewBox="0 0 24 24"
                                        stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                            </p>

                            <!-- Expandable Details -->
                            <Transition enter-active-class="transition-all duration-300 ease-out"
                                enter-from-class="max-h-0 opacity-0" enter-to-class="max-h-96 opacity-100"
                                leave-active-class="transition-all duration-200 ease-in"
                                leave-from-class="max-h-96 opacity-100" leave-to-class="max-h-0 opacity-0">
                                <div v-if="showDetails" class="mt-4 overflow-hidden">
                                    <div class="rounded-lg bg-gray-50 p-4">
                                        <h4 class="text-sm font-semibold text-gray-900">What cookies do we use?</h4>
                                        <ul class="mt-2 space-y-2 text-sm text-gray-600">
                                            <li class="flex items-start gap-2">
                                                <svg class="mt-0.5 h-5 w-5 shrink-0 text-blue-500" fill="currentColor"
                                                    viewBox="0 0 20 20">
                                                    <path fill-rule="evenodd"
                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                        clip-rule="evenodd" />
                                                </svg>
                                                <span><strong>Analytics cookies:</strong> Help us understand how
                                                    visitors interact with our website</span>
                                            </li>
                                            <li class="flex items-start gap-2">
                                                <svg class="mt-0.5 h-5 w-5 shrink-0 text-blue-500" fill="currentColor"
                                                    viewBox="0 0 20 20">
                                                    <path fill-rule="evenodd"
                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                        clip-rule="evenodd" />
                                                </svg>
                                                <span><strong>Marketing cookies:</strong> Used to track visitors across
                                                    websites for advertising purposes</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </Transition>
                        </div>

                        <!-- Action Buttons -->
                        <div
                            class="mt-6 flex shrink-0 flex-col-reverse gap-3 sm:flex-row md:ml-auto md:mt-0 md:flex-col">
                            <button @click="handleAccept" type="button"
                                class="inline-flex cursor-pointer items-center justify-center rounded-theme-element text-white bg-primary-500 hover:bg-primary-600 px-6 py-3 text-sm font-semibold text-primary-overlay transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:text-base">
                                <svg class="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M5 13l4 4L19 7" />
                                </svg>
                                Accept All
                            </button>
                            <button @click="handleDeny" type="button"
                                class="inline-flex items-center justify-center rounded-theme-element border cursor-pointer border-gray-300 bg-white px-6 py-3 text-sm font-medium text-gray-700 shadow-sm transition-all duration-200 hover:bg-gray-50 hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:text-base">
                                <svg class="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Reject
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Transition>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import router from '@/router';
import { createTrackingManager } from './UseTrackingCodes';

const props = defineProps({
    metaPixelId: {
        type: String,
        default: ''
    },
    googleTagManagerId: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['accept', 'deny']);

const showBanner = ref(false);
const showDetails = ref(false);

let trackingManager = null;
let removeRouterHook = null;

onMounted(() => {
    // Initialize tracking manager with custom showBanner function
    trackingManager = createTrackingManager({
        metaPixelId: props.metaPixelId,
        googleTagManagerId: props.googleTagManagerId,
        showBanner: () => {
            showBanner.value = true;
        }
    });

    // Initialize tracking (will show banner if no consent decision made)
    trackingManager.initialize();

    removeRouterHook = router.afterEach((to, from) => {
        if (from.matched.length === 0) {
            return;
        }
        trackingManager.trackVirtualPageView({
            path: to.fullPath,
            title: to.meta?.title || document.title,
            pageLocation: typeof window !== 'undefined' ? window.location.href : '',
        });
    });
});

onUnmounted(() => {
    removeRouterHook?.();
    removeRouterHook = null;
});

const handleAccept = () => {
    trackingManager.acceptCookies();
    showBanner.value = false;
    emit('accept');
};

const handleDeny = () => {
    trackingManager.denyCookies();
    showBanner.value = false;
    emit('deny');
};

// Expose methods for parent components
defineExpose({
    clearConsent: () => {
        if (trackingManager) {
            trackingManager.clearConsent();
            showBanner.value = true;
        }
    },
    getConsentStatus: () => trackingManager?.getConsentStatus(),
    trackEvent: (eventName, eventData) => trackingManager?.trackEvent(eventName, eventData),
    trackVirtualPageView: (payload) => trackingManager?.trackVirtualPageView(payload),
    StandardTrackEvents: () => trackingManager?.StandardTrackEvents
});
</script>